import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { UtilityService } from '../../../services/utility/utility.service';
import { User, UserRole } from '../../models/user';

export interface InviteUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  welcomeNote?: string;
}

/**
 * Admin-only user management against the LoginApi /Users endpoints.
 * (Distinct from the auth-flow UserService.)
 */
@Injectable({ providedIn: 'root' })
export class AdminUsersService {
  private utility = inject(UtilityService);
  private readonly endpoint = environment.loginApiUrl + '/Users';

  getAll(): Observable<User[]> {
    return this.utility.getRequest<User[]>(`${this.endpoint}/All`);
  }

  invite(request: InviteUserRequest): Observable<User> {
    // Names are collected in the invite dialog and persisted on the invite record,
    // so a first sign-in with a token that carries no name claims (e.g. a
    // Google-federated account) still resolves to a properly-named user.
    return this.utility.postRequest<User>(`${this.endpoint}/Invite`, {
      email: request.email,
      firstName: request.firstName,
      lastName: request.lastName,
      role: request.role,
      welcomeNote: request.welcomeNote || null,
    });
  }

  changeRole(userId: number, role: UserRole): Observable<User> {
    return this.utility.patchRequest<User>(`${this.endpoint}/${userId}/Role`, { role });
  }

  deactivate(userId: number): Observable<User> {
    return this.utility.patchRequest<User>(`${this.endpoint}/${userId}/Deactivate`);
  }

  reactivate(userId: number): Observable<User> {
    return this.utility.patchRequest<User>(`${this.endpoint}/${userId}/Reactivate`);
  }

  remove(userId: number): Observable<unknown> {
    return this.utility.deleteRequest<unknown>(`${this.endpoint}/${userId}`);
  }

  resendInvite(userId: number): Observable<User> {
    return this.utility.postRequest<User>(`${this.endpoint}/${userId}/ResendInvite`, {});
  }
}
